import { useState } from "react";
import { CountryWithMCC } from "@/src/types/country";
import { useLocale, useTranslations } from "next-intl";
import { useGetCountryDataQuery } from "@/src/api/rtkQuery/countryApi";
import { Box, Button, FormControlLabel, InputLabel, Paper, Radio, RadioGroup, Typography } from "@mui/material";
import ItineraryAutoComplete from "../auto-complete";
import ItineraryTextField from "../itinearyTextField";
import { useLazyGetTravellersByEmailOrMobileQuery } from "@/src/api/rtkQuery/itineraryApi";
import { User } from "@/src/types/itinerary";

interface Props {
    setUserTravellerData: (user?: User) => void;
    userTravellerData?: User;
};

const ProfileSearchWrapper = (props: Props) => {
    const userOptions = ['Guest User', 'Registered User'];
    const [selectedUser, setSelectedUser] = useState(userOptions[0]);
    const [agentDetails, setAgentDetails] = useState({ email: "", mobileNumber: "" });
    const [errors, setErrors] = useState({ email: "", mobileNumber: "" });
    const [touched, setTouched] = useState({ email: false, mobileNumber: false });
    const userIdentity = props?.userTravellerData?.customer?.email || (props?.userTravellerData?.phoneNumbers[0]?.mobileNumber)
    const [noDataFoundForTheUser,setNoDataFoundForTheUser] = useState(false);
    const onUserChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedUser((event.target as HTMLInputElement).value);
        // Reset form when switching user types
        setAgentDetails({ email: "", mobileNumber: "" });
        setErrors({ email: "", mobileNumber: "" });
        setTouched({ email: false, mobileNumber: false });
    };

    const defaultMobileCountry: CountryWithMCC = {
        "countryCode": "SA",
        "en": "Saudi Arabia",
        "ar": "المملكة العربية السعودية",
        "mobileCountryCode": "966",
        "category": "",
    };

    const [selectedCountry, setSelectedCountry] = useState<CountryWithMCC>(defaultMobileCountry);
    const isRegisteredUser = selectedUser === 'Registered User';
    const locale = useLocale();
    const t = useTranslations('itineraryPage.contactDetails');
    const validation = useTranslations('validation');
    const commonTranslate = useTranslations('common');
    const { data: countryData, isLoading: isCountryDataLoading } = useGetCountryDataQuery();
    const { email, mobileNumber } = agentDetails;
    const [triggerUserData, { isLoading: isProfileSearchLoading }] =
        useLazyGetTravellersByEmailOrMobileQuery();


    // Validation helper function to get mobile validation length
    const getMobileValidationLength = (mobileCountryCode: string): { min: number; max: number } => {
        // Add country-specific validation rules here
        // Example lengths for common countries
        const countryLengths: Record<string, { min: number; max: number }> = {
            '966': { min: 9, max: 9 },  // Saudi Arabia
            '971': { min: 9, max: 9 },  // UAE
            '91': { min: 10, max: 10 }, // India
            '1': { min: 10, max: 10 },  // US/Canada
        };
        return countryLengths[mobileCountryCode] || { min: 7, max: 15 }; // Default
    };

    // Validation functions
    const validateEmail = (value: string): string | null => {
        if (!value) return null; // Let required rule handle empty check
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            return validation('pleaseEnterValidEmailAddress') || 'Please enter a valid email address';
        }
        return null;
    };

    const validatePhone = (value: { mobile: string; mobileCountryCode: string }): string | null => {
        // Only numbers allowed
        const phoneRegex = /^\d*$/;
        if (!phoneRegex.test(value.mobile)) {
            return validation('pleaseEnterValidPhoneNumber') || 'Please enter a valid phone number';
        }
        const mobileValidationLength = getMobileValidationLength(value.mobileCountryCode);
        if (value.mobile.length < mobileValidationLength.min || value.mobile.length > mobileValidationLength.max) {
            return validation('pleaseEnterValidPhoneNumber') || 'Please enter a valid phone number';
        }
        return null;
    };

    const handleCountryCodeChanges = (value: CountryWithMCC | null) => {
        const newCountry = value ?? defaultMobileCountry;
        setSelectedCountry(newCountry);

        if (agentDetails.mobileNumber) {
            const phoneError = validatePhone({
                mobile: agentDetails.mobileNumber,
                mobileCountryCode: newCountry.mobileCountryCode
            });

            setErrors(prev => ({
                ...prev,
                mobileNumber: phoneError || ''
            }));
        }
    }


    const handleSearch = async () => {
        if (!isRegisteredUser) return;

        // Reset errors
        setErrors({ email: "", mobileNumber: "" });

        let hasError = false;
        const newErrors = { email: "", mobileNumber: "" };

        // Validate email if provided
        if (email) {
            const emailError = validateEmail(email);
            if (emailError) {
                newErrors.email = emailError;
                hasError = true;
            }
        }

        // Validate mobile number if provided
        if (mobileNumber) {
            const phoneError = validatePhone({
                mobile: mobileNumber,
                mobileCountryCode: selectedCountry.mobileCountryCode
            });
            if (phoneError) {
                newErrors.mobileNumber = phoneError;
                hasError = true;
            }
        }

        if (hasError) {
            setErrors(newErrors);
            return;
        }

        const userApiResponse = await triggerUserData({ email, mobileNumber, mobileCode: selectedCountry.mobileCountryCode });
        const User = userApiResponse?.data;
        if (User?.statusCode === 401) {
            props.setUserTravellerData(undefined);
            setNoDataFoundForTheUser(true);
        }else {
            props.setUserTravellerData(User);
            setNoDataFoundForTheUser(false);
        }

    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAgentDetails({
            ...agentDetails,
            email: event.target.value
        });
        // Clear email error when user starts typing
        if (errors.email) {
            setErrors({ ...errors, email: "" });
        }
    };

    const handleEmailBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        setTouched({ ...touched, email: true });
        const value = event.target.value;
        if (value) {
            const emailError = validateEmail(value);
            if (emailError) {
                setErrors({ ...errors, email: emailError });
            }
        }
    };

    const handleMobileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Only allow numeric input
        const value = event.target.value.replace(/\D/g, '');
        setAgentDetails({
            ...agentDetails,
            mobileNumber: value
        });
        // Clear mobile error when user starts typing
        if (errors.mobileNumber) {
            setErrors({ ...errors, mobileNumber: "" });
        }
    };

    const handleMobileBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        setTouched({ ...touched, mobileNumber: true });
        const value = event.target.value;
        if (value) {
            const phoneError = validatePhone({
                mobile: value,
                mobileCountryCode: selectedCountry.mobileCountryCode
            });
            if (phoneError) {
                setErrors({ ...errors, mobileNumber: phoneError });
            }
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'borders.default',
                p: { xs: 1.5, sm: 2 },      // smaller padding on mobile
                mb: 1,
                width: '100%',
            }}
        >
            {/* USER TYPE RADIO BUTTONS */}
            <Box sx={{ mb: 0, pl: { xs: 0.5, sm: 1 } }}>
                <RadioGroup
                    row
                    onChange={onUserChange}
                    value={selectedUser || ''}
                    sx={{
                        alignItems: 'center',
                        flexWrap: 'wrap',       // wrap on small screens
                        gap: { xs: 1, sm: 0 }
                    }}
                >
                    {userOptions.map((user: any) => (
                        <FormControlLabel
                            key={user}
                            value={user}
                            control={<Radio size="small" />}
                            label={
                                <Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 600 }}>
                                    {user}
                                </Typography>
                            }
                            sx={{
                                '.MuiFormControlLabel-label': {
                                    fontSize: { xs: 13, sm: 15 },
                                    fontWeight: 500
                                }
                            }}
                        />
                    ))}
                </RadioGroup>
            </Box>

            {/* REGISTERED USER FORM */}
            {isRegisteredUser && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { xs: 2, md: 2 },
                        mt: '1rem',
                        ml: { xs: 0, sm: '0.5rem' }
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },   // stack on mobile
                            gap: { xs: 2.5, md: 4 },
                            alignItems: 'flex-start'
                        }}
                    >
                        {/* EMAIL FIELD */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                flex: { xs: 1, md: 0.8 },
                                width: '100%'
                            }}
                        >
                            <InputLabel shrink htmlFor="contact-email" sx={{ fontWeight: 600, fontSize: 16, color: '#374151' }}>
                                {t('emailAddress')}
                            </InputLabel>
                            <ItineraryTextField
                                type="email"
                                id="contact-email"
                                placeholder={t('emailPlaceholder')}
                                value={email}
                                onChange={handleEmailChange}
                                onBlur={handleEmailBlur}
                                error={touched.email && !!errors.email}
                                helperText={touched.email && errors.email}
                            />
                        </Box>

                        {/* MOBILE NUMBER ROW */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'flex-start',
                                flex: 1,
                                width: '100%',
                                flexDirection: { xs: 'column', sm: 'row' }   // Mobile stacked
                            }}
                        >
                            {/* COUNTRY CODE */}
                            <Box sx={{ flex: { xs: 1, sm: 0.5 }, width: '100%' }}>
                                <ItineraryAutoComplete
                                    disableClearable
                                    label={t('mobileNumber')}
                                    loading={isCountryDataLoading}
                                    options={countryData || []}
                                    value={selectedCountry}
                                    onChange={(value) => handleCountryCodeChanges(value)}
                                    getOptionLabel={option =>
                                        `+ ${option.mobileCountryCode} (${option[locale as keyof CountryWithMCC]})`
                                    }
                                    renderValueForMobileScreen={value => `+ ${value?.mobileCountryCode}`}
                                    renderOption={(props: any, option) => (
                                        <Typography {...props} key={props.key} sx={{ fontSize: 13 }}>
                                            {`+ ${option.mobileCountryCode} (${option[locale as keyof CountryWithMCC]})`}
                                        </Typography>
                                    )}
                                    placeholder={t('select')}
                                    groupBy={e => commonTranslate(e.category)}
                                />
                            </Box>

                            {/* MOBILE NUMBER FIELD */}
                            <Box sx={{ flex: { xs: 1, sm: 2 }, width: '100%' }}>
                                <InputLabel
                                    shrink
                                    htmlFor="mobile-number"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: 16,
                                        color: 'transparent',
                                        userSelect: 'none'
                                    }}
                                >
                                    {t('mobileNumber')}
                                </InputLabel>
                                <ItineraryTextField
                                    inputProps={{
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*'
                                    }}
                                    id="mobile-number"
                                    placeholder={t('mobilePlaceholder')}
                                    value={mobileNumber}
                                    onChange={handleMobileChange}
                                    onBlur={handleMobileBlur}
                                    error={touched.mobileNumber && !!errors.mobileNumber}
                                    helperText={touched.mobileNumber && errors.mobileNumber}
                                />
                            </Box>
                        </Box>

                        {/* SEARCH BUTTON */}
                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            disabled={
                                isProfileSearchLoading ||
                                (!email && !mobileNumber) ||
                                Boolean(errors.email || errors.mobileNumber)
                            }
                            sx={theme => ({
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: '0.5rem',
                                boxShadow: 'none',
                                height: 'fit-content',
                                mt: { xs: 0, md: '1.3rem' },
                                width: { xs: '100%', sm: 'auto' },   // full width on mobile
                                '&:hover': {
                                    backgroundColor: theme.palette.secondary.main,
                                    color: theme.palette.primary.main,
                                    boxShadow: 'none'
                                },
                                '&:disabled': {
                                    opacity: 0.6
                                }
                            })}
                        >
                            {commonTranslate(isProfileSearchLoading ? 'Loading' : 'searchProfiles')}
                        </Button>
                    </Box>
                    {userIdentity &&
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', color: '#16a34a', fontWeight: '500' }}>
                                {commonTranslate('successUserDetailsMessage', { email: userIdentity })}
                            </Typography>
                        </Box>
                    }
                    {noDataFoundForTheUser &&
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', color: '#c83f3f', fontWeight: '400' }}>
                                {commonTranslate('noUserDetailsFound')}
                            </Typography>
                        </Box>
                    }
                </Box>
            )}
        </Paper>
    );
};

export default ProfileSearchWrapper;